import {
	DataSecurityWithAllFeaturesAndRanksTct,
	DayCountConvention,
	PeriodUnitType,
	SecurityExchangeRightsPeriodBasis,
	SecurityExchangeTransactionType,
	SecurityIssuedIn,
} from '@dealstack/shared/lib/generated';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';

import {
	SecurityExchangeRightsFormDto,
	SecurityExchangeRightsShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/ExchangeRights/exchangeRights.zod.ts';
import { getDealSecurities } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { makeSecurityPillOptions } from '@dealstack/frontend/shared/incomeRights.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { useQuery } from '@tanstack/react-query';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

export enum EXCHANGE_RIGHTS_FORM_FIELD {
	transactionType = 'transactionType',
	exchangedForSecurityId = 'exchangedForSecurityId',
	rate = 'rate',
	periodBasis = 'periodBasis',
	periodBasisDate = 'periodBasisDate',
	periodBasisIntervalStartDuration = 'periodBasisIntervalStartDuration',
	periodBasisIntervalStartPeriod = 'periodBasisIntervalStartPeriod',
	periodBasisIntervalEndDuration = 'periodBasisIntervalEndDuration',
	periodBasisIntervalEndPeriod = 'periodBasisIntervalEndPeriod',
	price = 'price',
	additionalPriceRate = 'additionalPriceRate',
	additionalPriceAccrualPeriod = 'additionalPriceAccrualPeriod',
	additionalPriceDayCountConvention = 'additionalPriceDayCountConvention',
	additionalPriceDeemedStartDate = 'additionalPriceDeemedStartDate',
	additionalPriceCompoundingPeriod = 'additionalPriceCompoundingPeriod',
	additionalPriceFirstInterestDate = 'additionalPriceFirstInterestDate',
	optionRank = 'optionRank',
}

export const useExchangeRightsController = (
	security: DataSecurityWithAllFeaturesAndRanksTct | null
) => {
	const tCapManage = useTranslationCapManage();
	const getDealSecuritiesRequest = useQuery({
		queryKey: [API_PATHS.SECURITY_DEAL_GET],
		queryFn: () => getDealSecurities(API_PATHS.SECURITY_DEAL_GET),
	});

	const defaultValues = useMemo(
		() => ({
			transactionType:
				security?.securityExchangeRights?.transactionType ??
				SecurityExchangeTransactionType.ISSUE,
			exchangedForSecurityId: security?.securityExchangeRights?.exchangedForSecurityId,
			rate: security?.securityExchangeRights?.rate ?? 1,
			periodBasis:
				security?.securityExchangeRights?.periodBasis ??
				SecurityExchangeRightsPeriodBasis.UNRESTRICTED,
			periodBasisDate: security?.securityExchangeRights?.periodBasisDate,
			periodBasisIntervalStartDuration:
				security?.securityExchangeRights?.periodBasisIntervalStartDuration ?? undefined,
			periodBasisIntervalStartPeriod:
				security?.securityExchangeRights?.periodBasisIntervalStartPeriod,
			periodBasisIntervalEndDuration:
				security?.securityExchangeRights?.periodBasisIntervalEndDuration ?? undefined,
			periodBasisIntervalEndPeriod:
				security?.securityExchangeRights?.periodBasisIntervalEndPeriod,
			price: security?.securityExchangeRights?.price,
			additionalPriceRate: security?.securityExchangeRights?.additionalPriceRate,
			additionalPriceAccrualPeriod:
				security?.securityExchangeRights?.additionalPriceAccrualPeriod ||
				PeriodUnitType.DAILY,
			additionalPriceDayCountConvention:
				security?.securityExchangeRights?.additionalPriceDayCountConvention ??
				DayCountConvention.ACTUAL_ACTUAL_ISDA,
			additionalPriceDeemedStartDate:
				security?.securityExchangeRights?.additionalPriceDeemedStartDate,
			additionalPriceCompoundingPeriod:
				security?.securityExchangeRights?.additionalPriceCompoundingPeriod,
			additionalPriceFirstInterestDate:
				security?.securityExchangeRights?.additionalPriceFirstInterestDate,
		}),
		[security]
	);
	const exchangeRightsForm = useForm<SecurityExchangeRightsFormDto>({
		mode: 'all',
		resolver: zodResolver(SecurityExchangeRightsShape(tCapManage)),
		defaultValues,
		shouldFocusError: false,
	});

	const transactionType = exchangeRightsForm.watch(EXCHANGE_RIGHTS_FORM_FIELD.transactionType);
	const periodBasis = exchangeRightsForm.watch(EXCHANGE_RIGHTS_FORM_FIELD.periodBasis);
	const prevPeriodBasisRef = useRef(periodBasis);

	const additionalPriceRate = exchangeRightsForm.watch(
		EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceRate
	);
	const prevAdditionalPriceRateRef = useRef(additionalPriceRate);

	// Clear disabled fields when switching period basis
	useEffect(() => {
		// Only clear when the value actually changes (not on initial mount)
		if (prevPeriodBasisRef.current === periodBasis) {
			return;
		}

		prevPeriodBasisRef.current = periodBasis;

		const setValueOptions = { shouldDirty: true, shouldTouch: true, shouldValidate: true };

		if (
			periodBasis === SecurityExchangeRightsPeriodBasis.UNRESTRICTED ||
			periodBasis === SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE
		) {
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.periodBasisDate,
				'',
				setValueOptions
			);
		}

		if (
			periodBasis === SecurityExchangeRightsPeriodBasis.UNRESTRICTED ||
			periodBasis === SecurityExchangeRightsPeriodBasis.FIXED_DATE
		) {
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.periodBasisIntervalStartDuration,
				'',
				setValueOptions
			);
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.periodBasisIntervalStartPeriod,
				undefined,
				setValueOptions
			);
		}

		if (periodBasis === SecurityExchangeRightsPeriodBasis.UNRESTRICTED) {
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.periodBasisIntervalEndDuration,
				'',
				setValueOptions
			);
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.periodBasisIntervalEndPeriod,
				undefined,
				setValueOptions
			);
		}
	}, [periodBasis, exchangeRightsForm]);

	// Clear additional price fields when rate becomes 0 or empty
	useEffect(() => {
		const wasNonZero =
			prevAdditionalPriceRateRef.current !== undefined &&
			prevAdditionalPriceRateRef.current !== null &&
			prevAdditionalPriceRateRef.current !== 0;
		const isNowZeroOrEmpty =
			additionalPriceRate === 0 ||
			additionalPriceRate === undefined ||
			additionalPriceRate === null;

		prevAdditionalPriceRateRef.current = additionalPriceRate;

		if (wasNonZero && isNowZeroOrEmpty) {
			const setValueOptions = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceAccrualPeriod,
				undefined,
				setValueOptions
			);
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceDayCountConvention,
				undefined,
				setValueOptions
			);
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceDeemedStartDate,
				'',
				setValueOptions
			);
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceCompoundingPeriod,
				null,
				setValueOptions
			);
			exchangeRightsForm.setValue(
				EXCHANGE_RIGHTS_FORM_FIELD.additionalPriceFirstInterestDate,
				'',
				setValueOptions
			);
		}
	}, [additionalPriceRate, exchangeRightsForm]);
	const currentExchangedForSecurityId = security?.securityExchangeRights?.exchangedForSecurityId;
	const dealSecurities = useMemo(
		() =>
			makeSecurityPillOptions(
				getDealSecuritiesRequest?.data?.filter((sec) => sec.id !== security?.id) || [],
				(sec) => {
					// Always include the currently selected target so it displays correctly
					if (sec.id === currentExchangedForSecurityId) {
						return true;
					}

					// Common filter: exclude securities that already have exchange rights
					if (sec.hasExchangeRights) {
						return false;
					}

					if (transactionType === SecurityExchangeTransactionType.ISSUE) {
						return (
							sec.issuedIn === SecurityIssuedIn.UNITS &&
							sec.issuerId === security?.issuerId
						);
					}

					// TRANSFER: allow all remaining securities
					return true;
				}
			),
		[
			getDealSecuritiesRequest?.data,
			transactionType,
			security?.id,
			security?.issuerId,
			currentExchangedForSecurityId,
		]
	);

	const resetForm = () => {
		exchangeRightsForm.reset(defaultValues);
	};

	const isAdditionalPriceRateZeroOrEmpty =
		additionalPriceRate === 0 ||
		additionalPriceRate === undefined ||
		additionalPriceRate === null;

	return {
		exchangeRightsForm,
		dealSecurities,
		resetForm,
		isAdditionalPriceRateZeroOrEmpty,
	};
};
export const makeExchangeRightsTransactionTypeOptions = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>
) =>
	Object.values(SecurityExchangeTransactionType).map((type) => ({
		label: getExchangeRightsTransactionTypeLabel(tCommonUi, type) ?? '',
		value: type,
	}));

export const getExchangeRightsTransactionTypeLabel = (
	tCommonUI: TranslateFunction<I18Namespaces.COMMON_UI>,
	type?: SecurityExchangeTransactionType
) => {
	if (!type) {
		return;
	}

	switch (type) {
		case SecurityExchangeTransactionType.ISSUE:
			return tCommonUI('security_exchange_rights_transaction_type.issue');
		case SecurityExchangeTransactionType.TRANSFER:
			return tCommonUI('security_exchange_rights_transaction_type.transfer');
		default:
			return type;
	}
};

export const makeExchangeRightsPeriodBasisOptions = (
	tCommonUi: TranslateFunction<I18Namespaces.COMMON_UI>
) =>
	Object.values(SecurityExchangeRightsPeriodBasis).map((basis) => ({
		label: getExchangeRightsPeriodBasisLabel(tCommonUi, basis) ?? '',
		value: basis,
	}));

export const getExchangeRightsPeriodBasisLabel = (
	tCommonUI: TranslateFunction<I18Namespaces.COMMON_UI>,
	basis?: SecurityExchangeRightsPeriodBasis
) => {
	if (!basis) {
		return;
	}

	switch (basis) {
		case SecurityExchangeRightsPeriodBasis.UNRESTRICTED:
			return tCommonUI('security_exchange_rights.period_basis.unrestricted');
		case SecurityExchangeRightsPeriodBasis.RELATIVE_TO_ISSUE_DATE:
			return tCommonUI('security_exchange_rights.period_basis.relative_to_issue_date');
		case SecurityExchangeRightsPeriodBasis.FIXED_DATE:
			return tCommonUI('security_exchange_rights.period_basis.fixed_date');
		default:
			return basis;
	}
};
